import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DoseTile } from "~/components/routine/DoseTile";
import { useToggleDose } from "~/components/routine/use-routine";
import { SNACKBAR_POSITION } from "~/components/ui/snackbar";
import { dayKey } from "~/lib/calendar";
import { tapFeedback } from "~/lib/capacitor";
import { isEditableDay, isFutureDay, type RoutineDay } from "~/lib/routine";

/**
 * Both doses of one day, side by side, morning first.
 *
 * Each tile owns its own mutation, so the morning can be ticked while the evening's
 * previous tap is still in flight, but one tile never races itself: it is disabled until
 * its own request has settled.
 */
export function DayDoses({
  day,
  today,
  doses,
  loading = false,
}: {
  day: Date;
  today: Date;
  doses: RoutineDay | undefined;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const morning = useToggleDose();
  const evening = useToggleDose();
  const [failed, setFailed] = useState(false);

  const key = dayKey(day);
  const readOnly = !isEditableDay(day, today) || isFutureDay(day, today);
  const isPast = key < dayKey(today);

  const toggle = (slot: "morning" | "evening") => {
    const taken = doses?.[slot] === undefined;
    if (taken) tapFeedback();
    (slot === "morning" ? morning : evening).mutate(
      { day: key, slot, taken },
      { onError: () => setFailed(true) },
    );
  };

  return (
    <>
      <Box
        data-testid={`routine.doses-${key}`}
        sx={{ display: "flex", gap: { xs: 1, md: 1.5 } }}
      >
        <DoseTile
          slot="morning"
          checkIn={doses?.morning}
          isPast={isPast}
          loading={loading}
          pending={morning.isPending}
          readOnly={readOnly}
          onToggle={() => toggle("morning")}
        />
        <DoseTile
          slot="evening"
          checkIn={doses?.evening}
          isPast={isPast}
          loading={loading}
          pending={evening.isPending}
          readOnly={readOnly}
          onToggle={() => toggle("evening")}
        />
      </Box>

      <Snackbar
        open={failed}
        autoHideDuration={5000}
        onClose={() => setFailed(false)}
        sx={SNACKBAR_POSITION}
      >
        <Alert severity="error" onClose={() => setFailed(false)}>
          {t("routine.saveError")}
        </Alert>
      </Snackbar>
    </>
  );
}
